/**
 * Robust error-detail extractor for admin surfaces.
 *
 * Backends return validation errors in several shapes; historically the
 * B4 frontend passed the raw `detail` object straight to
 * `toast.error(...)` / `setErr(...)`, which crashes React when the
 * shape is `{errors: [...]}` (see D5 Errata 4).
 *
 * This helper reduces every observed shape to a short human-readable
 * string. NEVER returns an object; callers can pass the result
 * directly to sonner or React children.
 *
 * Contract:
 *   undefined / null            → "Something went wrong"
 *   Error / AxiosError (bare)   → error.message | fallback
 *   string                      → returned verbatim
 *   { errors: [{field,message}] } → "field: message · field: message"
 *   [{ msg, loc:[...] }]        → "loc: msg · loc: msg"   (raw FastAPI 422)
 *   { error: "…" }              → "…"
 *   { detail: "…" }             → "…"   (defensive)
 *   { detail: <object|array> }  → recurses into detail
 *   any other object            → JSON.stringify truncated to 240 chars
 */

const FALLBACK = "Something went wrong";
const MAX_JSON_LEN = 240;

function _truncate(s) {
    if (typeof s !== "string") return String(s);
    return s.length > MAX_JSON_LEN ? s.slice(0, MAX_JSON_LEN - 1) + "…" : s;
}

function _fromDetail(detail) {
    if (detail == null) return null;
    if (typeof detail === "string") return detail;

    // FastAPI 422 raw: Array<{msg, loc:[...], type}>
    if (Array.isArray(detail)) {
        const parts = detail
            .filter((x) => x && typeof x === "object")
            .map((x) => {
                const loc = Array.isArray(x.loc)
                    ? x.loc.filter((l) => l !== "body").join(".") : x.field;
                const msg = x.msg || x.message || x.error || "invalid";
                return loc ? `${loc}: ${msg}` : msg;
            })
            .filter(Boolean);
        return parts.length ? parts.join(" · ") : null;
    }

    if (typeof detail === "object") {
        // Custom stage-5 shape: {errors: [{field,message}]}
        if (Array.isArray(detail.errors)) {
            const parts = detail.errors
                .filter((x) => x && typeof x === "object")
                .map((x) => {
                    const f = x.field || x.loc || "";
                    const m = x.message || x.msg || x.error || "invalid";
                    return f ? `${f}: ${m}` : m;
                })
                .filter(Boolean);
            if (parts.length) return parts.join(" · ");
        }
        if (typeof detail.error === "string" && detail.error) return detail.error;
        if (typeof detail.message === "string" && detail.message) return detail.message;
        try {
            return _truncate(JSON.stringify(detail));
        } catch (_) { return null; }
    }

    return null;
}

export function readErrorDetail(err, fallback = FALLBACK) {
    if (err == null) return fallback;
    if (typeof err === "string") return err;

    // Axios error: err.response.data.detail is the primary shape.
    const responseData = err?.response?.data;
    if (responseData !== undefined) {
        const fromDetail = _fromDetail(responseData?.detail);
        if (fromDetail) return fromDetail;
        // Some endpoints skip the `detail` wrapper.
        const fromRoot = _fromDetail(responseData);
        if (fromRoot) return fromRoot;
    }

    if (typeof err?.message === "string" && err.message) return err.message;
    if (typeof err === "object") {
        try {
            return _truncate(JSON.stringify(err));
        } catch (_) { /* fall through */ }
    }

    return fallback;
}

export default readErrorDetail;
